import React, { useState } from "react";
import * as Yup from "yup";
import ButtonWithProgress from "components/ButtonWithProgress";
import { ReactComponent as VideoIcon2 } from "assets/img/icons/video-camera_2.svg";
import GeneralService from "services/GeneralService";
import { useFormik } from "formik";
import AttachmentSelection from "components/AttachmentSelection/AttachmentSelection";
import AttachmentPreview from "components/AttachmentPreview/AttachmentPreview";
import TeamService from "services/TeamService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { useToasts } from "react-toast-notifications";
import { useGlobalContext } from "store/contexts/globalContext";
import CustomTextArea from "components/CustomTextArea";
import classNames from "classnames";
import { useExpressionsContext } from "store/contexts/expressionsContext";
import { useRepliesContext } from "store/contexts/repliesContext";
import ChatGptAssistModal from "components/ChatGptAssistModal";

export default ({ post, expression }) => {
  const { microsoftTeamsVideoText } = useGlobalContext();
  const [uploadProgress, setUploadProgress] = useState(0);
  const { addToast } = useToasts();
  const { updateExpression } = useExpressionsContext();
  const { createReply } = useRepliesContext();

  const formik = useFormik({
    initialValues: {
      comment: "",
      attachment: null,
    },
    validateOnBlur: false,
    validationSchema: Yup.object().shape(
      {
        comment: Yup.string().max(5000, "Reply should not be greater then 5000").when(["attachment"], {
          is: (attachment) => {
            return !attachment;
          },
          then: Yup.string()
            .required("Please write comment or use an attachment."),
        }),
        attachment: Yup.mixed().when(["comment"], {
          is: (comment) => {
            return !comment;
          },
          then: Yup.mixed().required(),
        }),
      },
      ["attachment", "comment"]
    ),
    onSubmit: async (values) => {
      try {
        let media = null;

        if (values.attachment) {
          const preSignedResponse = await GeneralService.getPresignedUploadURL({
            filename: values.attachment.name,
            mime_type: values.attachment.type,
            file_size: values.attachment.size,
            resource_type: "team",
            resource_id: post.team_id,
            resource_policy: "expression",
          });

          const { key, url } = preSignedResponse.data.data;

          media = key;

          await GeneralService.uploadToS3(
            url,
            values.attachment,
            (progress) => {
              setUploadProgress(progress);
            }
          );
        }

        setUploadProgress(100);

        const response = await TeamService.createPostExpression(
          post.team_id,
          post.id,
          {
            comment: values.comment,
            media,
            attachment: values.attachment,
          },
          {
            parent_expression_id: expression.id,
          }
        );

        if (values.attachment) {
          var attachmentType = values.attachment.type.split("/");
        }

        formik.resetForm();
        setUploadProgress(0);

        if (
          attachmentType &&
          attachmentType[0] === "video" &&
          attachmentType[1] !== "mp4" &&
          !microsoftTeamsVideoText
        ) {
          addToast(
            "Your comment is being processed. We’ll let you know when it’s ready to view. ",
            { appearance: "info" }
          );
          return;
        }

        const expressionCopy = { ...expression };
        expressionCopy.replies_count += 1;

        updateExpression(expressionCopy);
        createReply(response.data.data);
      } catch (e) {
        console.log(e);
      }
    },
  });

  const onAttachmentSelected = (file) => {
    formik.setFieldValue("attachment", file);
  };

  const clearAttachment = () => {
    formik.setFieldValue("attachment", null);
  };

  const setValue = (value) => {
    formik.setFieldValue("comment", value);
  };

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <div className="writeComment">
        <div className="createCommentContent">
          <CustomTextArea
            scrollActivate={true}
            errorClass="textStyle"
            textAreaClass="form-control write-comments"
            formik={formik}
            placeholder="Write a comment"
            fieldName={"comment"}
            rows={1}
          />
          <div
            className={classNames({
              commentsIcon: true,
              commentsIconError: formik.errors.comment ? true : false,
            })}
          >
            <ChatGptAssistModal setValue={setValue} />
            <AttachmentSelection
              onChange={onAttachmentSelected}
              VideoAttachmentIcon={VideoIcon2}
              classAttatchment="p-attachments pl-15"
              videoSpanClass="p-video pl-15"
              attachmentIconColor="inherit"
              attachmentClass={"mwh-16"}
              videocamIconColor="inherit"
              videoClass={"mwh-16 icon-light"}
              allowDocuments={true}
              accepted={[
                ".png",
                ".jpg",
                ".jpeg",
                ".mp4",
                ".mp3",
                ".webm",
                ".pdf",
                ".doc",
                ".docx",
                ".xls",
                ".xlsx",
                ".ppt",
                ".pptx",
                ".txt",
                ".rtf",
                ".csv",
              ]}
            />
            {!formik.isSubmitting && (
              <div>
                <button
                  type="submit"
                  className="createCommentButton"
                  disabled={formik.isSubmitting}
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                </button>
              </div>
            )}
            {formik.isSubmitting && (
              <ButtonWithProgress
                initialText={"Submit"}
                buttonWithProgressClass="commentArrowButton"
                isSubmitting={true}
                progress={uploadProgress}
                message={""}
                iconfontSize={"16px"}
                overlay={false}
              />
            )}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            borderTop: "none",
          }}
        >
          {!!formik.values.attachment && (
            <AttachmentPreview
              commentAttachmentPreview={"createCommentAttachmentPreview"}
              attachment={formik.values.attachment}
              clearAttachment={clearAttachment}
              commentDocumentPreview={"createCommentDocumentPreview"}
            />
          )}
        </div>
      </div>
    </form>
  );
};
